import { useState } from "react";
import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";

type Assignment = { assignedToUserId: string; version: number };
type AssignmentResult = { message: string; refreshFailed: boolean };
class AssignmentFailure extends Error {
  constructor(message: string, readonly refreshFailed: boolean) { super(message); }
}

export function TradeAssignment({ trade, users }: {
  trade: { id: string; tradeId: string; assignedToUserId: string | null; version: number };
  users: { id: string; label: string; role: string }[] | undefined;
}) {
  const queryClient = useQueryClient();
  const mutationKey = ["eex-trade-assignment", trade.id];
  const [reloadNotice, setReloadNotice] = useState<string | null>(null);
  // Keep the pending selection AND its outcome when details are closed while
  // the request runs. Component-local mutation state would lose that feedback.
  const latest = useMutationState({
    filters: { mutationKey, exact: true },
    select: mutation => ({
      status: mutation.state.status,
      value: mutation.state.variables as Assignment | undefined,
      result: mutation.state.data as AssignmentResult | undefined,
      error: mutation.state.error as AssignmentFailure | null,
    }),
  }).at(-1);
  const pending = latest?.status === "pending";
  const refreshFailed = !!(latest?.result?.refreshFailed || latest?.error?.refreshFailed);
  const refresh = () => queryClient.invalidateQueries(
    { queryKey: ["eex-trades"] }, { throwOnError: true },
  );
  const assignment = useMutation({
    mutationKey,
    mutationFn: async (value: Assignment): Promise<AssignmentResult> => {
      try {
        await api<{ ok: boolean; unchanged?: boolean }>(`/eex/trades/${trade.id}/assign`, {
          method: "POST", body: JSON.stringify(value),
        });
      } catch (error) {
        let message = error instanceof Error ? error.message : "Assignment could not be saved.";
        // Fastify puts source-guard explanations in message, with a generic
        // status label (e.g. "Conflict") in error.
        if (error instanceof ApiError && error.body && typeof error.body === "object" &&
            "message" in error.body && typeof error.body.message === "string") message = error.body.message;
        let refreshFailed = false;
        if (error instanceof ApiError && error.status === 409) {
          try {
            await refresh();
            message += " Latest trade loaded. Review the assignment before retrying.";
          } catch {
            refreshFailed = true;
            message += " The latest trade could not be loaded. Reload before retrying.";
          }
        }
        throw new AssignmentFailure(message, refreshFailed);
      }
      // Refresh this blotter now; Positions/P&L reload on their next visit.
      await queryClient.invalidateQueries({
        predicate: query => typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith("eex-"),
        refetchType: "none",
      });
      try {
        await refresh();
        return { refreshFailed: false, message: `Assignment saved: ${users?.find(user => user.id === value.assignedToUserId)?.label ?? "selected trader"}.` };
      } catch {
        return { refreshFailed: true, message: "Assignment saved, but the latest trade could not be loaded. Reload before making another change." };
      }
    },
  });
  const feedback = latest?.status === "error" ? latest.error?.message
    : latest?.status === "success" ? latest.result?.message : reloadNotice;
  return <div className="space-y-1 text-xs">
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted">Reassign to:</span>
      <select
        aria-label={`Trader assignment for ${trade.tradeId}`}
        aria-busy={pending}
        value={(pending || (refreshFailed && latest?.status === "success")) ? latest?.value?.assignedToUserId : trade.assignedToUserId ?? ""}
        disabled={pending || refreshFailed || !users}
        onChange={event => {
          if (queryClient.isMutating({ mutationKey, exact: true }) || refreshFailed) return;
          setReloadNotice(null);
          assignment.mutate({ assignedToUserId: event.target.value, version: trade.version });
        }}
        className="bg-transparent border border-border/60 rounded px-2 py-1 disabled:opacity-50"
      >
        <option value="" disabled>choose trader…</option>
        {(users ?? []).map(user => <option key={user.id} value={user.id}>{user.label} ({user.role})</option>)}
      </select>
      {pending ? <span role="status">Saving assignment…</span> : <span className="text-muted">(reassigning also confirms)</span>}
    </div>
    {!pending && feedback && <p role={latest?.status === "error" ? "alert" : "status"} className={latest?.status === "error" ? "text-negRed" : "text-muted"}>{feedback}</p>}
    {refreshFailed && <button type="button" className="text-accent underline" onClick={async () => {
      try {
        await refresh();
        for (const mutation of queryClient.getMutationCache().findAll({ mutationKey, exact: true })) {
          if (mutation.state.status !== "pending") queryClient.getMutationCache().remove(mutation);
        }
        setReloadNotice("Latest trade loaded. Review the assignment before making another change.");
      } catch { /* Keep the reload action and existing explanation visible. */ }
    }}>Reload latest trade</button>}
  </div>;
}
